import { useState } from "react";
import { Button, Empty } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import PrismTruncatedText from "./PrismTruncatedText";

const AXIS_TICKS = [0, 25, 50, 75, 100];

const PrismOfficeDistributionChart = ({ data = [] }) => {
  const [collapsedKeys, setCollapsedKeys] = useState([]);
  const total = data.reduce((sum, item) => sum + item.count, 0);

  if (total === 0) {
    return (
      <Empty
        description="No office data available."
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const toggleCollapsed = (officeKey) => {
    setCollapsedKeys((currentKeys) =>
      currentKeys.includes(officeKey)
        ? currentKeys.filter((key) => key !== officeKey)
        : [...currentKeys, officeKey],
    );
  };

  return (
    <div className="prism-office-chart">
      <div className="prism-office-chart__row prism-office-chart__row--axis">
        <span className="prism-office-chart__toggle-spacer" />
        <span className="prism-office-chart__label" />
        <div className="prism-office-chart__axis">
          {AXIS_TICKS.map((tick) => (
            <span className="prism-office-chart__tick" key={tick}>
              {tick}%
            </span>
          ))}
        </div>
        <span className="prism-office-chart__value" />
      </div>

      {data.map((item) => {
        const percent = Math.round((item.count / total) * 100);
        const hasChildren = item.children?.length > 0;
        const isExpanded = hasChildren && !collapsedKeys.includes(item.key);

        return (
          <div className="prism-office-chart__group" key={item.key}>
            <div className="prism-office-chart__row">
              {hasChildren ? (
                <Button
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.label}`}
                  className="prism-office-chart__toggle"
                  icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                  onClick={() => toggleCollapsed(item.key)}
                  size="small"
                  type="text"
                />
              ) : (
                <span className="prism-office-chart__toggle-spacer" />
              )}
              <PrismTruncatedText
                className="prism-office-chart__label"
                value={item.label}
              />
              <div
                className="prism-office-chart__track"
                title={`${item.label}: ${item.count} subscription${item.count === 1 ? "" : "s"} (${percent}%)`}
              >
                <div
                  className="prism-office-chart__bar"
                  style={{ background: item.color, width: `${percent}%` }}
                />
              </div>
              <span className="prism-office-chart__value">
                {item.count} ({percent}%)
              </span>
            </div>

            {isExpanded && (
              <div className="prism-office-chart__children">
                {item.children.map((child) => {
                  const childPercent = item.count
                    ? Math.round((child.count / item.count) * 100)
                    : 0;

                  return (
                    <div className="prism-office-chart__row prism-office-chart__row--child" key={child.key}>
                      <span className="prism-office-chart__toggle-spacer" />
                      <PrismTruncatedText
                        className="prism-office-chart__label prism-office-chart__label--child"
                        value={child.label}
                      />
                      <div
                        className="prism-office-chart__track"
                        title={`${child.label}: ${child.count} subscription${child.count === 1 ? "" : "s"} (${childPercent}% of ${item.label})`}
                      >
                        <div
                          className="prism-office-chart__bar prism-office-chart__bar--child"
                          style={{ background: item.color, width: `${childPercent}%` }}
                        />
                      </div>
                      <span className="prism-office-chart__value">
                        {child.count} ({childPercent}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PrismOfficeDistributionChart;
